import type { ButtonHTMLAttributes, ReactNode } from "react"
import { cn } from "@/shared/lib/cn"

export type ButtonVariant = "primary" | "secondary" | "gold" | "text"
export type ButtonSize = "sm" | "md" | "lg"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
  children: ReactNode
}

const BASE =
  "inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 cursor-pointer select-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:pointer-events-none"

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-verde-profundo text-white hover:bg-verde-medio active:scale-[0.98] focus-visible:outline-dourado",
  secondary:
    "bg-transparent border-2 border-verde-profundo text-verde-profundo hover:bg-verde-profundo hover:text-white active:scale-[0.98] focus-visible:outline-verde-profundo",
  // Variante do CTA final: no prototipo ela era o `primary` com sete classes de
  // override no call site. Como aparece em mais de um lugar, virou variante.
  gold: "bg-dourado text-white border-0 hover:bg-dourado-claro active:scale-[0.98] focus-visible:outline-dourado-claro",
  text: "bg-transparent text-verde-profundo underline-offset-4 hover:underline focus-visible:outline-verde-profundo rounded-none",
}

const SIZES: Record<ButtonSize, string> = {
  sm: "h-10 px-4 text-sm",
  md: "h-12 px-6 text-base",
  lg: "h-14 px-8 text-base",
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  children,
  className,
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      // `type` default "button": dentro de um <form>, o default do HTML e
      // "submit", e um botao decorativo passaria a enviar o formulario.
      type={type}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : null}
      {children}
      {!loading && icon ? icon : null}
    </button>
  )
}
